import { ExternalLink } from "lucide-react";
import { SponsoredLabel } from "@/components/monetization/sponsored-label";

/** Creative fields the card renders (a subset of `PaidCampaignView`). */
export type PaidCampaignCardCreative = {
	headline: string;
	body: string;
	ctaText: string;
	ctaUrl: string;
	accent: string;
};

interface Props {
	campaign: PaidCampaignCardCreative;
	/** AdSlot zone name — only used for the "advertise here" tracking source. */
	slot: string;
	locale?: string;
	onCtaClick?: () => void;
	/**
	 * Builder preview: the CTA renders as a plain span so an advertiser poking
	 * at the mock never navigates or fires click tracking.
	 */
	preview?: boolean;
}

/**
 * The paid-campaign creative, shared by the live `AdSlotClient` and the
 * /advertise builder preview so what the advertiser sees is what ships.
 */
export function PaidCampaignCard({
	campaign: c,
	slot,
	locale,
	onCtaClick,
	preview = false,
}: Props) {
	const loc = locale === "en" ? "en" : "id";
	return (
		<div className="overflow-hidden rounded-2xl border border-border/60 bg-card shadow-sm">
			<div className="h-1 w-full" style={{ backgroundColor: c.accent }} />
			<div className="p-5">
				<div className="mb-2 flex items-center justify-end">
					<SponsoredLabel
						locale={loc}
						advertiseHref={`/${loc}/advertise?src=${slot}`}
					/>
				</div>
				<p className="text-sm font-semibold text-foreground">{c.headline}</p>
				{c.body ? (
					<p className="mt-1 text-xs leading-relaxed text-muted-foreground">
						{c.body}
					</p>
				) : null}
				{preview ? (
					// Builder mock: a plain span, not an inert `href="#"` link — the
					// CI link audit flagged the /advertise "Learn more" as a link
					// that goes nowhere (2026-10-25).
					<span
						data-testid="campaign-cta-preview"
						className="mt-4 inline-flex min-h-[44px] items-center gap-1.5 rounded-full px-4 py-2 text-xs font-semibold text-white"
						style={{ backgroundColor: c.accent }}
					>
						{c.ctaText}
						<ExternalLink className="h-3 w-3" aria-hidden="true" />
					</span>
				) : (
					<a
						href={c.ctaUrl}
						target="_blank"
						rel="sponsored noopener noreferrer"
						onClick={onCtaClick}
						className="mt-4 inline-flex min-h-[44px] items-center gap-1.5 rounded-full px-4 py-2 text-xs font-semibold text-white transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
						style={{ backgroundColor: c.accent }}
					>
						{c.ctaText}
						<ExternalLink className="h-3 w-3" aria-hidden="true" />
					</a>
				)}
			</div>
		</div>
	);
}
